"use client";

/**
 * Discard-preview confirm Modal (spec §6.1).
 *
 * Mounted only when the user attempts to leave the preview view (via
 * the close `×` button OR `Esc`) AND any candidate has at least one
 * override applied. Click-through behaviour:
 *
 *   - "Keep editing" → close the confirm; user stays on the preview.
 *   - "Discard"      → fire `onConfirm()`, which calls
 *     `useEnrollmentPreview.discardPreview()` upstream and routes the
 *     user back to the sequence detail page.
 *
 * The modal uses our custom `Modal` (NOT shadcn `Dialog`) so the close
 * affordance, sizing, and motion match every other destructive
 * confirmation in the app — see `frontend/src/components/ui/modal.tsx`.
 */

import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";

export interface DiscardConfirmProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Async so callers can `await` the discard mutation before closing. */
  onConfirm: () => Promise<void> | void;
  /** Drives the "Discard" button's `loading` state. */
  isPending?: boolean;
}

export function DiscardConfirm({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: DiscardConfirmProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm">
        <ModalHeader
          title="Discard preview?"
          iconName="IconCircleInfo"
        />
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            Per-candidate edits will be lost. This can&apos;t be undone. Open
            a fresh preview to start over.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Keep editing
          </Button>
          <SubmitButton
            type="button"
            variant="destructive"
            size="sm"
            loading={Boolean(isPending)}
            loadingLabel="Discard"
            onClick={onConfirm}
          >
            Discard
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
